import { useEffect, useState } from "react";
import { api, formatError, fmtINR } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Copy, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";

export default function AdminStores() {
  const [stores, setStores] = useState([]);
  const [plans, setPlans] = useState([]);
  const [open, setOpen] = useState(false);
  const [credsOpen, setCredsOpen] = useState(null);
  const [form, setForm] = useState({ name: "", owner_name: "", owner_email: "", mobile: "", address: "", city: "", state: "", pincode: "", plan_id: "", subscription_days: 30 });

  const load = () => api.get("/admin/stores").then((r) => setStores(r.data));
  useEffect(() => { load(); api.get("/admin/plans").then((r) => setPlans(r.data)); }, []);

  const submit = async () => {
    try {
      const { data } = await api.post("/admin/stores", form);
      toast.success("Store created");
      setCredsOpen(data);
      setOpen(false);
      setForm({ name: "", owner_name: "", owner_email: "", mobile: "", address: "", city: "", state: "", pincode: "", plan_id: "", subscription_days: 30 });
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const toggle = async (s) => {
    const newStatus = s.status === "active" ? "inactive" : "active";
    await api.patch(`/admin/stores/${s.id}/status?status=${newStatus}`);
    toast.success(`Store ${newStatus}`);
    load();
  };

  return (
    <div className="p-4 sm:p-6 md:p-8 space-y-6" data-testid="admin-stores-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Stores</h1>
          <p className="text-slate-500">All Xerox shops on your platform</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-orange-500 hover:bg-orange-600" data-testid="btn-create-store"><Plus className="w-4 h-4 mr-2" />New Store</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle className="font-display">Create Xerox Store</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Store name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="store-name" /></div>
              <div><Label>Owner name</Label><Input value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} data-testid="store-owner-name" /></div>
              <div><Label>Mobile</Label><Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} data-testid="store-mobile" /></div>
              <div className="col-span-2"><Label>Owner email</Label><Input type="email" value={form.owner_email} onChange={(e) => setForm({ ...form, owner_email: e.target.value })} data-testid="store-email" /></div>
              <div className="col-span-2"><Label>Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
              <div><Label>City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
              <div><Label>State</Label><Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} /></div>
              <div><Label>Pincode</Label><Input value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} /></div>
              <div><Label>Sub. days</Label><Input type="number" value={form.subscription_days} onChange={(e) => setForm({ ...form, subscription_days: +e.target.value })} /></div>
              <div className="col-span-2"><Label>Plan</Label>
                <Select value={form.plan_id} onValueChange={(v) => setForm({ ...form, plan_id: v })}>
                  <SelectTrigger data-testid="store-plan"><SelectValue placeholder="Select plan" /></SelectTrigger>
                  <SelectContent>{plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} — {fmtINR(p.price)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter><Button onClick={submit} className="bg-orange-500 hover:bg-orange-600" data-testid="store-submit">Create Store</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-slate-200"><CardContent className="p-0 overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Store</TableHead><TableHead>Owner</TableHead><TableHead>City</TableHead><TableHead>Plan</TableHead><TableHead>Status</TableHead><TableHead>Orders</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {stores.map((s) => (
              <TableRow key={s.id} data-testid={`store-row-${s.slug}`}>
                <TableCell>
                  <div className="font-medium">{s.name}</div>
                  <div className="text-xs text-slate-400 font-mono">/{s.slug}</div>
                </TableCell>
                <TableCell>
                  <div>{s.owner_name}</div>
                  <div className="text-xs text-slate-400">{s.owner_email}</div>
                </TableCell>
                <TableCell>{s.city || "—"}</TableCell>
                <TableCell>{s.subscription?.plan_name || "—"}</TableCell>
                <TableCell><Badge variant={s.status === "active" ? "default" : "secondary"} className={s.status === "active" ? "bg-emerald-500" : ""}>{s.status}</Badge></TableCell>
                <TableCell className="font-mono">{s.order_count}</TableCell>
                <TableCell className="flex gap-1">
                  <Link to={`/s/${s.slug}`} target="_blank"><Button size="sm" variant="ghost"><ExternalLink className="w-3.5 h-3.5" /></Button></Link>
                  <Button size="sm" variant="outline" onClick={() => toggle(s)} data-testid={`toggle-${s.slug}`}>{s.status === "active" ? "Deactivate" : "Activate"}</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>

      <Dialog open={!!credsOpen} onOpenChange={(o) => !o && setCredsOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">Store credentials</DialogTitle></DialogHeader>
          {credsOpen && <div className="space-y-3">
            <p className="text-sm text-slate-500">Share these with the owner. They must change the password on first login.</p>
            <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 space-y-2 font-mono text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Email</span><span data-testid="new-store-email">{credsOpen.owner_email}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Password</span><span data-testid="new-store-password">{credsOpen.default_password}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Public URL</span><span>/s/{credsOpen.store.slug}</span></div>
            </div>
            <Button variant="outline" onClick={() => { navigator.clipboard.writeText(`Email: ${credsOpen.owner_email}\nPassword: ${credsOpen.default_password}`); toast.success("Copied"); }}><Copy className="w-4 h-4 mr-2" />Copy</Button>
          </div>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
